import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface DiscrepancyWarehouseConfig {
  code: string;
  name: string;
}

export interface DiscrepancyClientConfig {
  name: string;
  code: string;
  warehouses: DiscrepancyWarehouseConfig[];
  columns: string[];
}

export interface DiscrepancyConfig {
  clients: DiscrepancyClientConfig[];
}

/** Struttura restituita da /clients (Opr_Clients JPA entity) */
export interface OprClientOption {
  clientCode: string;
  clientName: string;
}

/** Struttura restituita da /warehouses (Opr_Warehouses JPA entity) */
export interface OprWarehouseOption {
  warehouseCode: string;
  warehouseName: string;
}

export interface DiscrepancySearchRequest {
  clientCode:    string | null;
  warehouseCode: string | null;
  dateFrom:      string | null;
  dateTo:        string | null;
  page:          number;
  size:          number;
  sortField:     string;
  sortAsc:       boolean;
}

export interface DiscrepancyRow {
  [key: string]: any;
}

export interface DiscrepancyPagedResponse {
  content:       DiscrepancyRow[];
  totalElements: number;
  totalPages?:   number;
  number?:       number;
  size?:         number;
}

/** Resoconto aggregato restituito da /summary per i filtri correnti. */
export interface DiscrepancySummary {
  colliAffidati:       number;
  colliPickerati:      number;
  colliNonPickerati:   number;
  colliNonAffidati:    number;
  colliDanneggiati:    number;
  immaginiDisponibili: number;
}

export interface SendLoadedToClientResult {
  processed:  number;
  sent:       number;
  clientCode: string;
  folderPath: string;
  error?:     string;
}

export interface SendUpdatesToClientResult {
  status:     string;
  operation:  string;
  clientCode: string;
  brandCode:  string;
  error?:     string;
}

/** Riepilogo restituito da /exportXmlToFolder */
export interface ExportXmlToFolderResult {
  clientCode:       string;
  folderPath:       string;
  waybillsFound:    number;
  xmlGenerated:     number;
  shipmentNotFound: string[];
  error?:           string;
}

/** Riepilogo restituito da /importDdtAttachmentsFromFolder */
export interface ImportDdtAttachmentsResult {
  folderPath:       string;
  filesProcessed:   number;
  rowsParsed:       number;
  waybillsUpdated:  number;
  skippedRows:      string[];
  notFoundWaybills: string[];
  failedFiles:      string[];
  error?:           string;
}

@Injectable({ providedIn: 'root' })
export class DiscrepancyListService {

  private readonly apiUrl = `${environment.apiUrl}/descrepancy-list`;

  private get httpOptions() {
    const token    = this.authService.getAccessToken();
    const language = localStorage.getItem('language') ?? 'it';
    return {
      headers: new HttpHeaders({
        'Content-Type':    'application/json',
        'Authorization':   `Bearer ${token}`,
        'Accept-language': language
      })
    };
  }

  constructor(private http: HttpClient, private authService: AuthService) {}

  /** Header con i filtri (dateFrom, dateTo, clientCode, warehouseCode); omessi se vuoti. */
  private buildFilterHeaders(request: DiscrepancySearchRequest): HttpHeaders {
    const token    = this.authService.getAccessToken();
    const language = localStorage.getItem('language') ?? 'it';
    let headers = new HttpHeaders({
      'Authorization':   `Bearer ${token}`,
      'Accept-language': language
    });
    if (request.dateFrom)      { headers = headers.set('dateFrom',      request.dateFrom); }
    if (request.dateTo)        { headers = headers.set('dateTo',        request.dateTo); }
    if (request.clientCode)    { headers = headers.set('clientCode',    request.clientCode); }
    if (request.warehouseCode) { headers = headers.set('warehouseCode', request.warehouseCode); }
    return headers;
  }

  /**
   * Recupera la configurazione DESCREPANCY_LIST.
   * Il backend restituisce un Opr_Configurations con configurationValue = JSON.
   * Sempre risolve (mai lancia): in caso di errore restituisce { clients: [] }.
   */
  getConfiguration(): Observable<DiscrepancyConfig> {
    return this.http.post<any>(`${this.apiUrl}/configuration`, {}, this.httpOptions).pipe(
      map(res => {
        if (res?.configurationValue) {
          try { return JSON.parse(res.configurationValue) as DiscrepancyConfig; } catch { /* invalid JSON */ }
        }
        // L'endpoint ha restituito direttamente l'oggetto configurazione senza configurationValue
        if (res?.clients) { return res as DiscrepancyConfig; }
        return { clients: [] };
      }),
      catchError(() => of({ clients: [] } as DiscrepancyConfig))
    );
  }

  /** Restituisce l'elenco di tutti i clienti (Opr_Clients). Sempre risolve. */
  getClients(): Observable<OprClientOption[]> {
    return this.http.post<OprClientOption[]>(`${this.apiUrl}/clients`, {}, this.httpOptions).pipe(
      catchError(() => of([] as OprClientOption[]))
    );
  }

  /** Restituisce l'elenco di tutti i magazzini (Opr_Warehouses). Sempre risolve. */
  getWarehouses(): Observable<OprWarehouseOption[]> {
    return this.http.post<OprWarehouseOption[]>(`${this.apiUrl}/warehouses`, {}, this.httpOptions).pipe(
      catchError(() => of([] as OprWarehouseOption[]))
    );
  }

  /** Resoconto aggregato (conteggi colli + immagini disponibili) per i filtri correnti. */
  getSummary(request: DiscrepancySearchRequest): Observable<DiscrepancySummary> {
    return this.http.post<DiscrepancySummary>(`${this.apiUrl}/summary`, request, this.httpOptions).pipe(
      catchError(err => {
        console.error('DiscrepancyListService /summary error:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Esporta in Excel (comparison list) i dati corrispondenti ai filtri correnti.
   * L'endpoint GET /excel riceve i filtri come header HTTP (dateFrom, dateTo,
   * clientCode, warehouseCode); gli header vengono omessi se il filtro è vuoto.
   */
  exportExcel(request: DiscrepancySearchRequest): Observable<Blob> {
    const headers = this.buildFilterHeaders(request);
    return this.http.get(`${this.apiUrl}/excel`, { headers, responseType: 'blob' as const }).pipe(
      catchError(err => {
        console.error('DiscrepancyListService /excel error:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Scarica lo zip delle immagini disponibili per i filtri correnti.
   * Stessi parametri di /excel (dateFrom, dateTo, clientCode, warehouseCode),
   * inviati sia come header HTTP sia nel body.
   */
  downloadImages(request: DiscrepancySearchRequest): Observable<Blob> {
    const headers = this.buildFilterHeaders(request);
    const body = {
      dateFrom:      request.dateFrom,
      dateTo:        request.dateTo,
      clientCode:    request.clientCode,
      warehouseCode: request.warehouseCode
    };
    return this.http.post(`${this.apiUrl}/download-images`, body, { headers, responseType: 'blob' as const }).pipe(
      catchError(err => {
        console.error('DiscrepancyListService /download-images error:', err);
        return throwError(() => err);
      })
    );
  }

  /** Ricerca paginata delle discrepanze. */
  search(request: DiscrepancySearchRequest): Observable<DiscrepancyPagedResponse> {
    return this.http.post<DiscrepancyPagedResponse>(`${this.apiUrl}/search`, request, this.httpOptions).pipe(
      catchError(err => {
        console.error('DiscrepancyListService /search error:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Genera gli XML "loaded to client" e marca le bolle come SENT_LOADED_TO_CLIENT.
   * Restituisce { processed, sent, clientCode, folderPath }.
   */
  sendLoadedToClient(): Observable<SendLoadedToClientResult> {
    return this.http.post<SendLoadedToClientResult>(`${this.apiUrl}/send-loaded-to-client`, {}, this.httpOptions).pipe(
      catchError(err => {
        console.error('DiscrepancyListService /send-loaded-to-client error:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Invia manualmente gli update di consegna dei colli al portale/sistema esterno.
   * I parametri del job sono costruiti lato backend: il frontend invia solo la richiesta.
   */
  sendUpdatesToClient(): Observable<SendUpdatesToClientResult> {
    return this.http.post<SendUpdatesToClientResult>(`${this.apiUrl}/send-updates-to-client`, {}, this.httpOptions).pipe(
      catchError(err => {
        console.error('DiscrepancyListService /send-updates-to-client error:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Invia manualmente gli update di consegna dei colli (operation SHIPMENTS) al portale esterno.
   * I parametri del job sono costruiti lato backend: il frontend invia solo la richiesta.
   */
  sendShipments(): Observable<SendUpdatesToClientResult> {
    return this.http.post<SendUpdatesToClientResult>(`${this.apiUrl}/send-shipments`, {}, this.httpOptions).pipe(
      catchError(err => {
        console.error('DiscrepancyListService /send-shipments error:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Invia manualmente gli update di consegna dei pallet (operation CLAIMS) al portale esterno.
   * I parametri del job sono costruiti lato backend: il frontend invia solo la richiesta.
   */
  sendClaims(): Observable<SendUpdatesToClientResult> {
    return this.http.post<SendUpdatesToClientResult>(`${this.apiUrl}/send-claims`, {}, this.httpOptions).pipe(
      catchError(err => {
        console.error('DiscrepancyListService /send-claims error:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Invia manualmente gli update (operation REASONCODE) al portale esterno.
   * I parametri del job sono costruiti lato backend: il frontend invia solo la richiesta.
   */
  sendReasonCode(): Observable<SendUpdatesToClientResult> {
    return this.http.post<SendUpdatesToClientResult>(`${this.apiUrl}/send-reasoncode`, {}, this.httpOptions).pipe(
      catchError(err => {
        console.error('DiscrepancyListService /send-reasoncode error:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Genera gli XML sulla cartella di destinazione (solo parte EDI di
   * exportDataToClient), senza invio POD e senza marcare le bolle come VIEWED.
   * Il body è vuoto: il backend usa i parametri di default.
   */
  exportXmlToFolder(): Observable<ExportXmlToFolderResult> {
    return this.http.post<ExportXmlToFolderResult>(`${this.apiUrl}/exportXmlToFolder`, {}, this.httpOptions).pipe(
      catchError(err => {
        console.error('DiscrepancyListService /exportXmlToFolder error:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Importa gli allegati DDT dai CSV nella cartella configurata lato backend
   * e aggiorna WAYBILL_DDT_ATTACHMENTS per le waybill parsate.
   * La cartella è costruita lato backend: il frontend invia solo la richiesta.
   */
  importDdtAttachmentsFromFolder(): Observable<ImportDdtAttachmentsResult> {
    return this.http.post<ImportDdtAttachmentsResult>(`${this.apiUrl}/importDdtAttachmentsFromFolder`, {}, this.httpOptions).pipe(
      catchError(err => {
        console.error('DiscrepancyListService /importDdtAttachmentsFromFolder error:', err);
        return throwError(() => err);
      })
    );
  }
}
